import React, { useState, useEffect, useRef } from 'react';

const Terminal = () => {
  // Load saved terminal state from localStorage
  const loadTerminalState = () => {
    try {
      const savedState = localStorage.getItem('webos-terminal-state');
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (e) {
      console.error('Failed to load terminal state:', e);
    }
    return {
      currentDir: '/',
      theme: 'default',
      aliases: {},
      commandHistory: []
    };
  };

  const initialState = loadTerminalState();

  const [history, setHistory] = useState([
    { type: 'output', content: 'WebTopOS Terminal v2.0' },
    { type: 'output', content: 'Type "help" to see available commands or "guide" for an interactive tutorial.' },
  ]);
  const [input, setInput] = useState('');
  const [currentDir, setCurrentDir] = useState(initialState.currentDir);
  const [theme, setTheme] = useState(initialState.theme); // terminal theme
  const [aliases, setAliases] = useState(initialState.aliases); // custom command shortcuts
  const [commandHistory, setCommandHistory] = useState(initialState.commandHistory); // searchable history
  const [historyIndex, setHistoryIndex] = useState(-1); // for navigating history
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);
  const [visualEffect, setVisualEffect] = useState(null); // for visual effects
  const [systemResources, setSystemResources] = useState({
    memory: 0,
    cpu: 0,
    storage: { used: 0, total: 100 }
  });
  
  const inputRef = useRef(null);
  const terminalRef = useRef(null);

  // Virtual file system - same structure as FileExplorer
  const [fileSystem, setFileSystem] = useState([]);

  // Command categories for help
  const commandCategories = {
    'Basic Commands': [
      { name: 'help', desc: 'Display help information' },
      { name: 'guide', desc: 'Interactive tutorial for new users' },
      { name: 'clear', desc: 'Clear the terminal screen' },
      { name: 'exit', desc: 'Close the terminal' }
    ],
    'File System': [
      { name: 'ls [path]', desc: 'List directory contents' },
      { name: 'cd <path>', desc: 'Change directory' },
      { name: 'pwd', desc: 'Print working directory' },
      { name: 'cat <file>', desc: 'Display file contents' },
      { name: 'mkdir <dir>', desc: 'Create a new directory' },
      { name: 'touch <file>', desc: 'Create a new empty file' },
      { name: 'rm <file/dir>', desc: 'Remove a file or directory' }
    ],
    'Web Integration': [
      { name: 'fetch <url>', desc: 'Download and display content from URL' },
      { name: 'screenshot', desc: 'Capture screenshot of current view' },
      { name: 'share <file/text>', desc: 'Generate shareable link' },
      { name: 'browse <query>', desc: 'Search the web from terminal' }
    ],
    'System Management': [
      { name: 'sysinfo', desc: 'Display system resource usage' },
      { name: 'process', desc: 'List running applications' },
      { name: 'kill <process-id>', desc: 'Terminate a running app' },
      { name: 'startup', desc: 'Manage startup applications' }
    ],
    'Developer Tools': [
      { name: 'inspect <element>', desc: 'Analyze DOM elements' },
      { name: 'debug <app>', desc: 'Launch debugging tools' },
      { name: 'api <service>', desc: 'Test API endpoints' },
      { name: 'benchmark', desc: 'Measure system performance' }
    ],
    'User Experience': [
      { name: 'theme <option>', desc: 'Change terminal appearance' },
      { name: 'alias <name>=<command>', desc: 'Create custom command shortcuts' },
      { name: 'history [filter]', desc: 'View command history' },
      { name: 'suggest', desc: 'Get command suggestions' }
    ],
    'Visual Effects': [
      { name: 'matrix', desc: 'Display Matrix-style animation' },
      { name: 'rain', desc: 'Show ASCII rain animation' },
      { name: 'visualize <data>', desc: 'Convert data to ASCII charts' },
      { name: '3d <model>', desc: 'Display simple 3D ASCII models' }
    ],
    'WebOS Features': [
      { name: 'sync <service>', desc: 'Sync with cloud storage' },
      { name: 'offline <app>', desc: 'Mark apps for offline use' },
      { name: 'persist', desc: 'Save terminal state' },
      { name: 'voice', desc: 'Activate voice commands' }
    ],
    'Network Tools': [
      { name: 'netstat', desc: 'Monitor network connections' },
      { name: 'ping <host>', desc: 'Test network connectivity' },
      { name: 'speed', desc: 'Measure connection speed' },
      { name: 'firewall', desc: 'Control network access' }
    ],
    'Utilities': [
      { name: 'echo <text>', desc: 'Display text' },
      { name: 'date', desc: 'Show current date and time' },
      { name: 'calc <expression>', desc: 'Calculate mathematical expressions' },
      { name: 'timer <seconds>', desc: 'Set a countdown timer' },
      { name: 'todo', desc: 'Simple todo list manager' }
    ]
  };

  // Theme options
  const themes = {
    default: { bg: 'rgba(10, 10, 20, 0.2)', text: '#0f0', prompt: '#0f0' },
    dark: { bg: '#000', text: '#fff', prompt: '#0f0' },
    light: { bg: '#f0f0f0', text: '#000', prompt: '#00f' },
    retro: { bg: '#000080', text: '#ffff00', prompt: '#ffffff' },
    cyberpunk: { bg: '#0b0b18', text: '#ff0090', prompt: '#00faff' },
    matrix: { bg: '#000', text: '#0f0', prompt: '#0f0' },
    hacker: { bg: '#000', text: '#0f0', prompt: '#f00' },
    minimal: { bg: '#111', text: '#aaa', prompt: '#fff' }
  };

  // Get current theme styles
  const getCurrentTheme = () => themes[theme] || themes.default;

  useEffect(() => {
    // Load files from localStorage
    const savedFiles = localStorage.getItem('webTopOS_files');
    if (savedFiles) {
      setFileSystem(JSON.parse(savedFiles));
    }
  }, []);

  useEffect(() => {
    // Scroll to bottom when history changes
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  useEffect(() => {
    // Focus input when component mounts
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Save terminal state to localStorage whenever important state changes
  useEffect(() => {
    const stateToSave = {
      currentDir,
      theme,
      aliases,
      commandHistory
    };
    localStorage.setItem('webos-terminal-state', JSON.stringify(stateToSave));
  }, [currentDir, theme, aliases, commandHistory]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0 && historyIndex < commandHistory.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      executeCommand();
    } else {
      handleKeyDown(e);
    }
  };

  const addToHistory = (entry) => {
    setHistory((prev) => [...prev, entry]);
  };

  const parsePathArgument = (path) => {
    if (!path) return currentDir;
    
    if (path.startsWith('/')) {
      return path;
    } else {
      return currentDir === '/' ? `/${path}` : `${currentDir}/${path}`;
    }
  };

  const findItemByPath = (path) => {
    if (path === '/') return { type: 'folder', children: fileSystem };
    
    const pathParts = path.split('/').filter(p => p);
    let currentLevel = fileSystem;
    let item = null;
    
    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      const isLastPart = i === pathParts.length - 1;
      
      if (isLastPart) {
        item = currentLevel.find(f => f.name === part);
        break;
      } else {
        const folder = currentLevel.find(f => f.name === part && f.type === 'folder');
        if (folder && folder.children) {
          currentLevel = folder.children;
        } else {
          return null;
        }
      }
    }
    
    return item;
  };

  const getDirectoryContents = (path) => {
    if (path === '/') return fileSystem;
    
    const pathParts = path.split('/').filter(p => p);
    let currentLevel = fileSystem;
    
    for (const part of pathParts) {
      const folder = currentLevel.find(f => f.name === part && f.type === 'folder');
      if (folder && folder.children) {
        currentLevel = folder.children;
      } else {
        return null;
      }
    }
    
    return currentLevel;
  };

  const executeCommand = () => {
    const trimmedInput = input.trim();
    if (!trimmedInput) return;
    
    // Check for aliases first
    let processedInput = trimmedInput;
    Object.entries(aliases).forEach(([alias, command]) => {
      if (processedInput.startsWith(alias + ' ')) {
        processedInput = processedInput.replace(alias, command);
      } else if (processedInput === alias) {
        processedInput = command;
      }
    });
    
    addToHistory({ type: 'input', content: `${currentDir}> ${trimmedInput}` });
    
    // Add to command history
    setCommandHistory(prev => [...prev, trimmedInput]);
    setHistoryIndex(-1);
    
    // Split by space but respect quotes
    const args = processedInput.match(/(?:[^\s"]+|"[^"]*")+/g) || [];
    const command = args[0]?.toLowerCase();
    
    switch (command) {
      case 'help':
        if (args[1]) {
          // Show help for specific command
          let found = false;
          for (const category in commandCategories) {
            const cmd = commandCategories[category].find(c => c.name.split(' ')[0] === args[1].toLowerCase());
            if (cmd) {
              addToHistory({ type: 'output', content: `${cmd.name} - ${cmd.desc}` });
              found = true;
              break;
            }
          }
          if (!found) {
            addToHistory({ type: 'output', content: `No help available for '${args[1]}'` });
          }
        } else {
          // Show all command categories
          addToHistory({ type: 'output', content: 'WebTopOS Terminal - Command Categories:' });
          Object.keys(commandCategories).forEach(category => {
            addToHistory({ type: 'output', content: `\n${category}:` });
            commandCategories[category].forEach(cmd => {
              addToHistory({ type: 'output', content: `  ${cmd.name.padEnd(20)} - ${cmd.desc}` });
            });
          });
          addToHistory({ type: 'output', content: '\nType "help <command>" for more information on a specific command.' });
          addToHistory({ type: 'output', content: 'Type "guide" for an interactive tutorial.' });
        }
        break;
      
      // === WEB INTEGRATION COMMANDS ===
      case 'fetch':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'fetch: missing URL argument' });
          break;
        }
        
        let url = args[1].replace(/"/g, '');
        if (!url.startsWith('http')) {
          url = 'https://' + url;
        }
        
        addToHistory({ type: 'output', content: `Fetching content from ${url}...` });
        
        try {
          fetch(url)
            .then(response => {
              if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
              }
              return response.text();
            })
            .then(data => {
              // Trim and display text content
              const plainText = data.replace(/<[^>]*>/g, ' ')
                                    .replace(/\s+/g, ' ')
                                    .trim()
                                    .substring(0, 1000);
              addToHistory({ type: 'output', content: `Content from ${url} (first 1000 chars):\n${plainText}...` });
            })
            .catch(error => {
              addToHistory({ type: 'output', content: `Error fetching ${url}: ${error.message}` });
            });
        } catch (error) {
          addToHistory({ type: 'output', content: `Invalid URL or fetch error: ${error.message}` });
        }
        break;
        
      case 'screenshot':
        addToHistory({ type: 'output', content: 'Capturing screenshot...' });
        
        // Simulate screenshot with CSS
        const terminalElement = terminalRef.current;
        if (terminalElement) {
          const originalStyle = terminalElement.style.filter;
          terminalElement.style.filter = 'invert(10%) brightness(150%)';
          
          setTimeout(() => {
            terminalElement.style.filter = originalStyle;
            addToHistory({ type: 'output', content: 'Screenshot captured and saved to /screenshots/screen_' + Date.now() + '.png' });
          }, 300);
        } else {
          addToHistory({ type: 'output', content: 'Failed to capture screenshot.' });
        }
        break;
        
      case 'share':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'share: missing content to share' });
          break;
        }
        
        const shareContent = args.slice(1).join(' ').replace(/"/g, '');
        const shareUrl = `https://example.com/share?content=${encodeURIComponent(shareContent)}`;
        addToHistory({ type: 'output', content: 'Generated shareable link:' });
        addToHistory({ type: 'output', content: shareUrl });
        
        // Create clipboard function
        addToHistory({ 
          type: 'output', 
          content: '(Link copied to clipboard)',
          style: { fontStyle: 'italic', opacity: 0.7 }
        });
        
        try {
          navigator.clipboard.writeText(shareUrl);
        } catch (err) {
          console.error('Failed to copy: ', err);
        }
        break;
        
      case 'browse':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'browse: missing search query' });
          break;
        }
        
        const searchQuery = args.slice(1).join(' ').replace(/"/g, '');
        addToHistory({ type: 'output', content: `Searching for: ${searchQuery}` });
        
        // Simulated search results
        setTimeout(() => {
          addToHistory({ 
            type: 'output', 
            content: '\n[SEARCH RESULTS]:\n'
          });
          
          addToHistory({ 
            type: 'output', 
            content: `1. ${searchQuery} - Official Website\n   https://example.com/${searchQuery.replace(/\s+/g, '-')}\n`
          });
          
          addToHistory({ 
            type: 'output', 
            content: `2. What is ${searchQuery}? - A Complete Guide\n   https://guide.example.com/about-${searchQuery.replace(/\s+/g, '-')}\n`
          });
          
          addToHistory({ 
            type: 'output', 
            content: `3. Top 10 Facts About ${searchQuery}\n   https://facts.example.com/top-${searchQuery.replace(/\s+/g, '-')}\n`
          });
          
          addToHistory({ 
            type: 'output', 
            content: '\nTo open a result, type: open <number>'
          });
        }, 500);
        break;
        
      // === SYSTEM MANAGEMENT COMMANDS ===
      case 'sysinfo':
        // Generate random system usage values
        const memory = Math.floor(Math.random() * 40) + 30; // 30-70%
        const cpu = Math.floor(Math.random() * 50) + 10; // 10-60%
        const storage = {
          used: Math.floor(Math.random() * 30) + 10, // 10-40 GB
          total: 64 // 64 GB
        };
        
        setSystemResources({ memory, cpu, storage });
        
        // Browser information
        const browserInfo = {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          cookiesEnabled: navigator.cookieEnabled,
          online: navigator.onLine
        };
        
        addToHistory({ type: 'output', content: '====== SYSTEM INFORMATION ======' });
        addToHistory({ type: 'output', content: `Memory Usage: ${memory}%` });
        addToHistory({ type: 'output', content: `CPU Usage: ${cpu}%` });
        addToHistory({ type: 'output', content: `Storage: ${storage.used}GB / ${storage.total}GB` });
        addToHistory({ type: 'output', content: `Browser: ${browserInfo.userAgent.split(' ').pop()}` });
        addToHistory({ type: 'output', content: `Platform: ${browserInfo.platform}` });
        addToHistory({ type: 'output', content: `Online Status: ${browserInfo.online ? 'Connected' : 'Disconnected'}` });
        addToHistory({ type: 'output', content: `Screen Resolution: ${window.screen.width}x${window.screen.height}` });
        addToHistory({ type: 'output', content: `Window Size: ${window.innerWidth}x${window.innerHeight}` });
        addToHistory({ type: 'output', content: '=============================' });
        break;
        
      case 'process':
        // Simulated list of running processes
        const processes = [
          { id: 1001, name: 'System', memory: '12MB', cpu: '1%' },
          { id: 1002, name: 'WebBrowser', memory: '210MB', cpu: '14%' },
          { id: 1003, name: 'Terminal', memory: '45MB', cpu: '3%' },
          { id: 1004, name: 'FileExplorer', memory: '68MB', cpu: '2%' },
          { id: 1005, name: 'PrivacyTube', memory: '187MB', cpu: '8%' }
        ];
        
        addToHistory({ type: 'output', content: 'PROCESS LIST:' });
        addToHistory({ 
          type: 'output', 
          content: 'PID\tNAME\t\tMEMORY\tCPU'
        });
        
        processes.forEach(proc => {
          addToHistory({ 
            type: 'output', 
            content: `${proc.id}\t${proc.name.padEnd(10)}\t${proc.memory}\t${proc.cpu}`
          });
        });
        break;
        
      case 'kill':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'kill: missing process ID' });
          break;
        }
        
        const pid = parseInt(args[1]);
        if (isNaN(pid)) {
          addToHistory({ type: 'output', content: `kill: invalid process ID: ${args[1]}` });
          break;
        }
        
        // Check if this is the terminal process (simulate self-destruction)
        if (pid === 1003) {
          addToHistory({ type: 'output', content: 'Cannot terminate the Terminal process.' });
        } else if (pid >= 1001 && pid <= 1005) {
          addToHistory({ type: 'output', content: `Process ${pid} terminated successfully.` });
        } else {
          addToHistory({ type: 'output', content: `No process found with ID: ${pid}` });
        }
        break;
        
      case 'startup':
        const startupApps = [
          { name: 'System Services', enabled: true, required: true },
          { name: 'WebBrowser', enabled: true, required: false },
          { name: 'Security Monitor', enabled: true, required: false }
        ];
        
        if (args[1] === 'disable' && args[2]) {
          const appName = args[2].replace(/"/g, '');
          const app = startupApps.find(a => a.name.toLowerCase() === appName.toLowerCase());
          
          if (app) {
            if (app.required) {
              addToHistory({ type: 'output', content: `Cannot disable required startup item: ${app.name}` });
            } else {
              addToHistory({ type: 'output', content: `${app.name} will no longer start automatically.` });
            }
          } else {
            addToHistory({ type: 'output', content: `Startup item not found: ${appName}` });
          }
        } else if (args[1] === 'enable' && args[2]) {
          const appName = args[2].replace(/"/g, '');
          const app = startupApps.find(a => a.name.toLowerCase() === appName.toLowerCase());
          
          if (app) {
            addToHistory({ type: 'output', content: `${app.name} will now start automatically.` });
          } else {
            addToHistory({ type: 'output', content: `Startup item not found: ${appName}` });
          }
        } else {
          addToHistory({ type: 'output', content: 'STARTUP ITEMS:' });
          startupApps.forEach(app => {
            addToHistory({ 
              type: 'output', 
              content: `${app.name.padEnd(20)} [${app.enabled ? 'ENABLED' : 'DISABLED'}]${app.required ? ' (Required)' : ''}`
            });
          });
          addToHistory({ type: 'output', content: '\nUsage: startup enable|disable <item>' });
        }
        break;
        
      // === DEVELOPER TOOLS COMMANDS ===
      case 'inspect':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'inspect: missing element selector' });
          break;
        }
        
        const selector = args.slice(1).join(' ').replace(/"/g, '');
        addToHistory({ type: 'output', content: `Inspecting element: ${selector}` });
        
        try {
          const elements = document.querySelectorAll(selector);
          if (elements.length === 0) {
            addToHistory({ type: 'output', content: 'No elements found matching this selector.' });
          } else {
            addToHistory({ type: 'output', content: `Found ${elements.length} matching element(s):` });
            
            for (let i = 0; i < Math.min(elements.length, 3); i++) {
              const el = elements[i];
              const classes = Array.from(el.classList).join('.');
              const id = el.id ? `#${el.id}` : '';
              const dimensions = `${el.offsetWidth}x${el.offsetHeight}`;
              
              addToHistory({ 
                type: 'output', 
                content: `Element ${i+1}: ${el.tagName.toLowerCase()}${id}${classes ? `.${classes}` : ''}\n  Size: ${dimensions}, Text: "${el.textContent.substring(0, 50)}${el.textContent.length > 50 ? '...' : ''}"`
              });
            }
            
            if (elements.length > 3) {
              addToHistory({ type: 'output', content: `... and ${elements.length - 3} more` });
            }
          }
        } catch (error) {
          addToHistory({ type: 'output', content: `Error inspecting elements: ${error.message}` });
        }
        break;
        
      case 'debug':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'debug: missing app name' });
          break;
        }
        
        const appName = args[1].replace(/"/g, '');
        addToHistory({ type: 'output', content: `Launching debug tools for: ${appName}` });
        addToHistory({ type: 'output', content: 'Debug session started. Type "debug stop" to end session.' });
        
        // Simulate debug output
        setTimeout(() => {
          addToHistory({ 
            type: 'output', 
            content: `[DEBUG] App "${appName}" loaded successfully.`,
            style: { color: '#00aaff' }
          });
          
          setTimeout(() => {
            addToHistory({ 
              type: 'output', 
              content: `[DEBUG] Component tree: 5 main components, 28 child components`,
              style: { color: '#00aaff' }
            });
          }, 1000);
          
          setTimeout(() => {
            addToHistory({ 
              type: 'output', 
              content: `[DEBUG] Memory usage - Heap: 24.8MB, DOM nodes: 187`,
              style: { color: '#00aaff' }
            });
          }, 2000);
        }, 500);
        break;
        
      case 'api':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'api: missing service name or endpoint' });
          break;
        }
        
        const service = args[1].replace(/"/g, '');
        const method = args[2] || 'GET';
        
        addToHistory({ type: 'output', content: `Testing API: ${service} (${method})` });
        
        setTimeout(() => {
          // Simulated API responses
          if (service.toLowerCase() === 'users') {
            addToHistory({ 
              type: 'output', 
              content: 'API Response (200 OK):\n{\n  "users": [\n    {"id": 1, "name": "User 1"},\n    {"id": 2, "name": "User 2"},\n    {"id": 3, "name": "User 3"}\n  ],\n  "total": 3\n}'
            });
          } else if (service.toLowerCase() === 'weather') {
            addToHistory({ 
              type: 'output', 
              content: 'API Response (200 OK):\n{\n  "temp": 22.5,\n  "condition": "Sunny",\n  "humidity": 45,\n  "wind": 8\n}'
            });
          } else {
            addToHistory({ 
              type: 'output', 
              content: `API Response (404 Not Found):\n{\n  "error": "Service '${service}' not found"\n}`
            });
          }
        }, 800);
        break;
        
      case 'benchmark':
        addToHistory({ type: 'output', content: 'Running system benchmark...' });
        
        // Start timing
        const benchmarkStart = performance.now();
        
        // Test operations
        setTimeout(() => {
          // DOM operations test
          const div = document.createElement('div');
          for (let i = 0; i < 1000; i++) {
            div.innerHTML += `<span>test ${i}</span>`;
          }
          
          // Math operations test
          let mathResult = 0;
          for (let i = 0; i < 100000; i++) {
            mathResult += Math.sqrt(i);
          }
          
          // Finish timing
          const benchmarkEnd = performance.now();
          const totalTime = (benchmarkEnd - benchmarkStart).toFixed(2);
          
          // Generate scores (simulated)
          const scores = {
            cpu: Math.floor(Math.random() * 500) + 500,
            memory: Math.floor(Math.random() * 400) + 600,
            graphics: Math.floor(Math.random() * 600) + 400,
            overall: Math.floor(Math.random() * 500) + 500
          };
          
          addToHistory({ type: 'output', content: 'Benchmark completed in ' + totalTime + ' ms' });
          addToHistory({ type: 'output', content: '===== BENCHMARK RESULTS =====' });
          addToHistory({ type: 'output', content: `CPU Score: ${scores.cpu}` });
          addToHistory({ type: 'output', content: `Memory Score: ${scores.memory}` });
          addToHistory({ type: 'output', content: `Graphics Score: ${scores.graphics}` });
          addToHistory({ type: 'output', content: `Overall Performance: ${scores.overall}` });
          addToHistory({ type: 'output', content: '===========================' });
        }, 1500);
        break;
        
      // === USER EXPERIENCE COMMANDS ===
      case 'theme':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'Available themes:' });
          Object.keys(themes).forEach(themeName => {
            const isActive = themeName === theme;
            addToHistory({ 
              type: 'output', 
              content: `  ${themeName}${isActive ? ' (active)' : ''}` 
            });
          });
          break;
        }
        
        const newTheme = args[1].toLowerCase();
        if (themes[newTheme]) {
          setTheme(newTheme);
          addToHistory({ type: 'output', content: `Theme changed to ${newTheme}` });
        } else {
          addToHistory({ type: 'output', content: `Theme '${newTheme}' not found. Type 'theme' to see available themes.` });
        }
        break;
        
      case 'alias':
        if (!args[1]) {
          // List all aliases
          if (Object.keys(aliases).length === 0) {
            addToHistory({ type: 'output', content: 'No aliases defined.' });
          } else {
            addToHistory({ type: 'output', content: 'Defined aliases:' });
            Object.entries(aliases).forEach(([alias, command]) => {
              addToHistory({ type: 'output', content: `  ${alias}='${command}'` });
            });
          }
          break;
        }
        
        // Handle alias definition: alias ll='ls -l'
        const aliasDefinition = args.slice(1).join(' ');
        const aliasMatch = aliasDefinition.match(/^([^=]+)=(.+)$/);
        
        if (aliasMatch) {
          const [, aliasName, aliasCommand] = aliasMatch;
          
          // Remove quotes if present
          const cleanCommand = aliasCommand.replace(/^['"]|['"]$/g, '');
          
          setAliases(prev => ({
            ...prev,
            [aliasName]: cleanCommand
          }));
          
          addToHistory({ type: 'output', content: `Alias created: ${aliasName}='${cleanCommand}'` });
        } else {
          addToHistory({ type: 'output', content: 'Invalid alias syntax. Use: alias name=command' });
        }
        break;
        
      case 'history':
        if (commandHistory.length === 0) {
          addToHistory({ type: 'output', content: 'Command history is empty.' });
          break;
        }
        
        if (args[1]) {
          // Filter history by search term
          const filter = args[1].toLowerCase();
          const filtered = commandHistory.filter(cmd => 
            cmd.toLowerCase().includes(filter)
          );
          
          if (filtered.length === 0) {
            addToHistory({ type: 'output', content: `No commands matching '${filter}' found in history.` });
          } else {
            addToHistory({ type: 'output', content: `Commands matching '${filter}':` });
            filtered.forEach((cmd, i) => {
              addToHistory({ type: 'output', content: `  ${i+1}  ${cmd}` });
            });
          }
        } else {
          // Show all history
          addToHistory({ type: 'output', content: 'Command history:' });
          commandHistory.forEach((cmd, i) => {
            addToHistory({ type: 'output', content: `  ${i+1}  ${cmd}` });
          });
        }
        break;
        
      case 'suggest':
        // Get suggestions based on history
        if (commandHistory.length < 2) {
          addToHistory({ type: 'output', content: 'Not enough command history to make suggestions. Try using more commands first.' });
          break;
        }
        
        addToHistory({ type: 'output', content: 'Based on your command history, you might want to try:' });
        
        // Find commands not used recently
        const usedCommands = new Set(commandHistory.map(cmd => cmd.split(' ')[0]));
        const suggestions = Object.values(commandCategories)
          .flat()
          .map(cmd => cmd.name.split(' ')[0])
          .filter(cmd => !usedCommands.has(cmd))
          .sort(() => 0.5 - Math.random())
          .slice(0, 3);
          
        suggestions.forEach(suggestion => {
          // Find the command in categories
          let description = '';
          for (const category in commandCategories) {
            const cmd = commandCategories[category].find(c => c.name.split(' ')[0] === suggestion);
            if (cmd) {
              description = cmd.desc;
              break;
            }
          }
          
          addToHistory({ 
            type: 'output', 
            content: `  ${suggestion.padEnd(12)} - ${description}`
          });
        });
        
        // Suggest combinations based on usage
        if (commandHistory.length > 5) {
          const lastCmd = commandHistory[commandHistory.length - 1].split(' ')[0];
          if (['ls', 'cat', 'cd'].includes(lastCmd)) {
            addToHistory({ 
              type: 'output', 
              content: `Tip: After using '${lastCmd}', users often run 'history' to review commands.`
            });
          }
        }
        break;

      // === VISUAL EFFECTS COMMANDS ===
      case 'matrix':
        setVisualEffect('matrix');
        addToHistory({ 
          type: 'special', 
          content: 'matrix',
          effect: 'matrix'
        });
        
        // Set a timeout to clear the effect
        setTimeout(() => {
          setVisualEffect(null);
          addToHistory({ type: 'output', content: '\nMatrix simulation ended.' });
        }, 8000);
        break;
        
      case 'rain':
        setVisualEffect('rain');
        
        const rainColors = args[1] || 'blue';
        
        addToHistory({ 
          type: 'special', 
          content: 'Digital rain simulation...',
          effect: 'rain',
          color: rainColors
        });
        
        // Set a timeout to clear the effect
        setTimeout(() => {
          setVisualEffect(null);
          addToHistory({ type: 'output', content: '\nRain simulation ended.' });
        }, 6000);
        break;
        
      case 'visualize':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'visualize: missing data to visualize' });
          break;
        }
        
        const data = args.slice(1).join(' ').replace(/"/g, '').split(',').map(Number);
        
        if (data.some(isNaN)) {
          addToHistory({ type: 'output', content: 'Invalid data format. Please provide comma-separated numbers.' });
          break;
        }
        
        // Create ASCII bar chart
        const max = Math.max(...data);
        const chartHeight = 5;
        let chart = '\n';
        
        // Generate bars
        for (let row = chartHeight; row > 0; row--) {
          chart += data.map(val => {
            const normalizedVal = (val / max) * chartHeight;
            return normalizedVal >= row ? '█' : ' ';
          }).join('  ') + '\n';
        }
        
        // Add baseline
        chart += data.map(() => '▀').join('  ') + '\n';
        
        // Add values
        chart += data.map(val => val.toString().padStart(2)).join(' ') + '\n';
        
        addToHistory({ 
          type: 'output', 
          content: 'ASCII Visualization:\n' + chart
        });
        break;
        
      case '3d':
        if (!args[1]) {
          addToHistory({ type: 'output', content: '3d: missing model name (cube, pyramid, sphere)' });
          break;
        }
        
        const model = args[1].toLowerCase();
        let ascii3d = '';
        
        if (model === 'cube') {
          ascii3d = `    +-----+\n   /|    /|\n  / |   / |\n +-----+  |\n |  |  |  |\n |  +--|--+\n | /   | /\n +-----+`;
        } else if (model === 'pyramid') {
          ascii3d = `     /\\\n    /  \\\n   /    \\\n  /      \\\n +--------+`;
        } else if (model === 'sphere') {
          ascii3d = `    .-"""-.\n  .'       '.\n /           \\\n|             |\n|             |\n\\            /\n  '.       .'\n    '-----'`;
        } else {
          addToHistory({ type: 'output', content: `Unknown model: ${model}. Available models: cube, pyramid, sphere` });
          break;
        }
        
        addToHistory({ 
          type: 'output', 
          content: `3D ASCII ${model}:\n${ascii3d}`
        });
        break;

      // === WEBOS FEATURES ===
      case 'sync':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'sync: missing service name (gdrive, dropbox, onedrive)' });
          break;
        }
        
        const syncService = args[1].toLowerCase();
        const validServices = ['gdrive', 'dropbox', 'onedrive', 'github'];
        
        if (!validServices.includes(syncService)) {
          addToHistory({ type: 'output', content: `Unknown service: ${syncService}. Available services: gdrive, dropbox, onedrive, github` });
          break;
        }
        
        addToHistory({ type: 'output', content: `Connecting to ${syncService}...` });
        
        setTimeout(() => {
          if (!navigator.onLine) {
            addToHistory({ type: 'output', content: 'Error: Cannot connect to sync service. Check your internet connection.' });
            return;
          }
          
          addToHistory({ type: 'output', content: `Connected to ${syncService}.` });
          
          setTimeout(() => {
            const filesCount = Math.floor(Math.random() * 10) + 5;
            addToHistory({ 
              type: 'output', 
              content: `Syncing ${filesCount} files...`
            });
            
            setTimeout(() => {
              addToHistory({ 
                type: 'output', 
                content: `✓ Successfully synchronized ${filesCount} files with ${syncService}.` 
              });
            }, 2000);
          }, 1000);
        }, 1500);
        break;
        
      case 'offline':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'Available apps for offline use:' });
          addToHistory({ type: 'output', content: '  notepad  - Text editor (Available offline)' });
          addToHistory({ type: 'output', content: '  terminal  - Command line (Available offline)' });
          addToHistory({ type: 'output', content: '  youtube   - Privacy Tube (Not available offline)' });
          addToHistory({ type: 'output', content: '  browser   - Web Browser (Partial offline support)' });
          break;
        }
        
        const offlineApp = args[1].toLowerCase();
        
        if (['youtube', 'browser'].includes(offlineApp)) {
          if (args[2] === 'enable') {
            addToHistory({ type: 'output', content: 'Downloading app resources for offline use...' });
            
            setTimeout(() => {
              addToHistory({ 
                type: 'output', 
                content: `The ${offlineApp} app is now available offline (100MB cached).`
              });
              addToHistory({ 
                type: 'output', 
                content: 'Note: Online features will require internet connection.'
              });
            }, 1500);
          } else if (args[2] === 'disable') {
            addToHistory({ type: 'output', content: `Clearing offline cache for ${offlineApp}...` });
            
            setTimeout(() => {
              addToHistory({ 
                type: 'output', 
                content: `The ${offlineApp} app will now require internet connection.`
              });
            }, 1000);
          } else {
            addToHistory({ type: 'output', content: `Usage: offline ${offlineApp} enable|disable` });
          }
        } else if (['notepad', 'terminal'].includes(offlineApp)) {
          addToHistory({ type: 'output', content: `The ${offlineApp} app is always available offline.` });
        } else {
          addToHistory({ type: 'output', content: `Unknown app: ${offlineApp}` });
        }
        break;
        
      case 'persist':
        if (!args[1]) {
          const persistentData = {
            commandHistory: true,
            theme: true,
            aliases: true
          };
          
          addToHistory({ type: 'output', content: 'Terminal persistence settings:' });
          Object.entries(persistentData).forEach(([setting, enabled]) => {
            addToHistory({ 
              type: 'output', 
              content: `  ${setting.padEnd(15)} [${enabled ? 'ENABLED' : 'DISABLED'}]`
            });
          });
          addToHistory({ type: 'output', content: '\nUsage: persist save|clear' });
          break;
        }
        
        if (args[1] === 'save') {
          addToHistory({ type: 'output', content: 'Saving terminal state to local storage...' });
          
          // Simulate saving terminal state
          setTimeout(() => {
            addToHistory({ 
              type: 'output', 
              content: 'Terminal state saved. Your settings will persist across sessions.'
            });
          }, 800);
        } else if (args[1] === 'clear') {
          addToHistory({ type: 'output', content: 'Clearing saved terminal state...' });
          
          setTimeout(() => {
            addToHistory({ 
              type: 'output', 
              content: 'Terminal state cleared. Your settings will reset on next session.'
            });
          }, 800);
        } else {
          addToHistory({ type: 'output', content: `Unknown option: ${args[1]}. Use: persist save|clear` });
        }
        break;
        
      case 'voice':
        if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
          addToHistory({ type: 'output', content: 'Speech recognition is not supported in your browser.' });
          break;
        }
        
        addToHistory({ type: 'output', content: 'Voice command mode activated. Speak after the beep...' });
        
        // Simulate voice recognition
        setTimeout(() => {
          const fakeCommands = ['list files', 'show system info', 'open browser'];
          const randomCommand = fakeCommands[Math.floor(Math.random() * fakeCommands.length)];
          
          addToHistory({ 
            type: 'output', 
            content: `Recognized: "${randomCommand}"`,
            style: { fontStyle: 'italic', color: '#aaa' }
          });
          
          // Execute the simulated command
          if (randomCommand === 'list files') {
            addToHistory({ type: 'input', content: `${currentDir}> ls` });
            
            const contents = getDirectoryContents(currentDir);
            if (contents && contents.length > 0) {
              const outputLines = contents.map(item => {
                const type = item.type === 'folder' ? 'dir' : 'file';
                return `${type}\t${item.name}`;
              });
              addToHistory({ type: 'output', content: outputLines.join('\n') });
            } else {
              addToHistory({ type: 'output', content: 'Directory is empty.' });
            }
          } else if (randomCommand === 'show system info') {
            addToHistory({ type: 'input', content: `${currentDir}> sysinfo` });
            
            const memory = Math.floor(Math.random() * 40) + 30;
            const cpu = Math.floor(Math.random() * 50) + 10;
            
            addToHistory({ type: 'output', content: '====== SYSTEM INFORMATION ======' });
            addToHistory({ type: 'output', content: `Memory Usage: ${memory}%` });
            addToHistory({ type: 'output', content: `CPU Usage: ${cpu}%` });
            addToHistory({ type: 'output', content: '=============================' });
          } else {
            addToHistory({ type: 'input', content: `${currentDir}> open browser` });
            addToHistory({ type: 'output', content: 'Attempting to open browser...' });
          }
        }, 1500);
        break;

      // === NETWORK TOOLS ===
      case 'netstat':
        addToHistory({ type: 'output', content: 'Active network connections:' });
        
        // Simulated active connections
        const connections = [
          { proto: 'TCP', local: '127.0.0.1:3000', remote: '127.0.0.1:56324', state: 'ESTABLISHED' },
          { proto: 'TCP', local: '192.168.1.5:443', remote: '172.217.21.78:https', state: 'ESTABLISHED' },
          { proto: 'UDP', local: '192.168.1.5:5353', remote: '*:*', state: 'LISTENING' },
        ];
        
        addToHistory({ 
          type: 'output', 
          content: 'Proto  Local Address          Remote Address         State'
        });
        
        connections.forEach(conn => {
          addToHistory({ 
            type: 'output', 
            content: `${conn.proto.padEnd(6)}${conn.local.padEnd(22)}${conn.remote.padEnd(22)}${conn.state}`
          });
        });
        
        // Display network interface
        const interfaces = [
          { name: 'eth0', ip: '192.168.1.5', mask: '255.255.255.0', status: 'UP' },
          { name: 'lo', ip: '127.0.0.1', mask: '255.0.0.0', status: 'UP' }
        ];
        
        addToHistory({ type: 'output', content: '\nNetwork interfaces:' });
        interfaces.forEach(intf => {
          addToHistory({ 
            type: 'output', 
            content: `${intf.name}: ${intf.ip} netmask ${intf.mask} status ${intf.status}`
          });
        });
        break;
        
      case 'ping':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'ping: missing host argument' });
          break;
        }
        
        const host = args[1].replace(/"/g, '');
        addToHistory({ type: 'output', content: `Pinging ${host}...` });
        
        // Check if we're online first
        if (!navigator.onLine) {
          addToHistory({ type: 'output', content: 'Network unreachable. Check your connection.' });
          break;
        }
        
        // Simulate ping responses
        const pingCount = 4;
        let currentPing = 0;
        
        const pingInterval = setInterval(() => {
          currentPing++;
          
          if (host === 'localhost' || host === '127.0.0.1') {
            const time = Math.random() * 5 + 1;
            addToHistory({ 
              type: 'output', 
              content: `Reply from ${host}: time=${time.toFixed(2)}ms`
            });
          } else if (host.match(/^192\.168\./)) {
            const time = Math.random() * 20 + 5;
            addToHistory({ 
              type: 'output', 
              content: `Reply from ${host}: time=${time.toFixed(2)}ms`
            });
          } else {
            // External host
            const time = Math.random() * 100 + 20;
            addToHistory({ 
              type: 'output', 
              content: `Reply from ${host}: time=${time.toFixed(2)}ms`
            });
          }
          
          if (currentPing >= pingCount) {
            clearInterval(pingInterval);
            
            // Show ping statistics
            const avgTime = Math.random() * 50 + 20;
            addToHistory({ 
              type: 'output', 
              content: `\nPing statistics for ${host}:\n    Packets: Sent = 4, Received = 4, Lost = 0 (0% loss),\nApproximate round trip times in milli-seconds:\n    Minimum = ${(avgTime * 0.8).toFixed(2)}ms, Maximum = ${(avgTime * 1.2).toFixed(2)}ms, Average = ${avgTime.toFixed(2)}ms`
            });
          }
        }, 1000);
        break;
        
      case 'speed':
        addToHistory({ type: 'output', content: 'Testing connection speed...' });
        
        if (!navigator.onLine) {
          addToHistory({ type: 'output', content: 'Error: You are offline. Cannot test connection speed.' });
          break;
        }
        
        let progress = 0;
        const progressInterval = setInterval(() => {
          progress += 10;
          addToHistory({ 
            type: 'output', 
            content: `Testing download speed... ${progress}%`,
            replace: true // Replace the previous line
          });
          
          if (progress >= 100) {
            clearInterval(progressInterval);
            
            // Get connection data if available
            let connectionType = 'Unknown';
            let downlinkSpeed = Math.random() * 50 + 10;
            
            const connection = navigator.connection || 
                         navigator.mozConnection || 
                         navigator.webkitConnection;
            
            if (connection) {
              connectionType = connection.effectiveType || 'Unknown';
              if (connection.downlink) {
                downlinkSpeed = connection.downlink;
              }
            }
            
            const uploadSpeed = downlinkSpeed * (Math.random() * 0.4 + 0.3);
            const latency = Math.random() * 100 + 10;
            
            addToHistory({ type: 'output', content: '\nSpeed test results:' });
            addToHistory({ type: 'output', content: `Connection type: ${connectionType}` });
            addToHistory({ type: 'output', content: `Download speed: ${downlinkSpeed.toFixed(2)} Mbps` });
            addToHistory({ type: 'output', content: `Upload speed: ${uploadSpeed.toFixed(2)} Mbps` });
            addToHistory({ type: 'output', content: `Latency: ${latency.toFixed(0)} ms` });
          }
        }, 200);
        break;
        
      case 'firewall':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'WebOS Firewall Status: Active' });
          addToHistory({ type: 'output', content: '\nApplication permissions:' });
          
          // Simulated app permissions
          const appPermissions = [
            { app: 'Browser', network: 'Allow', notifications: 'Allow', location: 'Ask' },
            { app: 'FileExplorer', network: 'Block', notifications: 'Block', location: 'Block' },
            { app: 'Terminal', network: 'Allow', notifications: 'Block', location: 'Block' },
            { app: 'PrivacyTube', network: 'Allow', notifications: 'Ask', location: 'Block' }
          ];
          
          addToHistory({ 
            type: 'output', 
            content: 'App'.padEnd(15) + 'Network'.padEnd(12) + 'Notifications'.padEnd(14) + 'Location'
          });
          
          appPermissions.forEach(app => {
            addToHistory({ 
              type: 'output', 
              content: `${app.app.padEnd(15)}${app.network.padEnd(12)}${app.notifications.padEnd(14)}${app.location}`
            });
          });
          
          addToHistory({ type: 'output', content: '\nUsage: firewall allow|block <app> <permission>' });
          break;
        }
        
        const action = args[1].toLowerCase();
        const firewallApp = args[2];
        const permission = args[3];
        
        if (!action || !firewallApp || !permission) {
          addToHistory({ type: 'output', content: 'Invalid syntax. Use: firewall allow|block <app> <permission>' });
          break;
        }
        
        if (action !== 'allow' && action !== 'block' && action !== 'ask') {
          addToHistory({ type: 'output', content: `Invalid action: ${action}. Use allow, block, or ask.` });
          break;
        }
        
        if (!['network', 'notifications', 'location', 'camera', 'microphone'].includes(permission)) {
          addToHistory({ type: 'output', content: `Invalid permission: ${permission}` });
          break;
        }
        
        addToHistory({ 
          type: 'output', 
          content: `Firewall rule updated: ${firewallApp} - ${permission} - ${action.toUpperCase()}`
        });
        break;

      // === ORIGINAL TERMINAL COMMANDS ===
      case 'ls':
        const lsPath = parsePathArgument(args[1]);
        const contents = getDirectoryContents(lsPath);
        
        if (contents) {
          if (contents.length === 0) {
            addToHistory({ type: 'output', content: 'Directory is empty.' });
          } else {
            const outputLines = contents.map(item => {
              const type = item.type === 'folder' ? 'dir' : 'file';
              return `${type}\t${item.name}`;
            });
            addToHistory({ type: 'output', content: outputLines.join('\n') });
          }
        } else {
          addToHistory({ type: 'output', content: `ls: cannot access '${lsPath}': No such file or directory` });
        }
        break;
      
      case 'cd':
        if (!args[1]) {
          setCurrentDir('/');
          break;
        }
        
        const cdPath = parsePathArgument(args[1]);
        const cdItem = cdPath === '/' ? { type: 'folder' } : findItemByPath(cdPath);
        
        if (cdItem && cdItem.type === 'folder') {
          setCurrentDir(cdPath);
        } else {
          addToHistory({ type: 'output', content: `cd: '${args[1]}': Not a directory` });
        }
        break;
      
      case 'pwd':
        addToHistory({ type: 'output', content: currentDir });
        break;
      
      case 'cat':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'cat: missing operand' });
          break;
        }
        
        const catPath = parsePathArgument(args[1]);
        const catItem = findItemByPath(catPath);
        
        if (catItem && catItem.type !== 'folder') {
          addToHistory({ type: 'output', content: catItem.content || '(empty file)' });
        } else if (catItem && catItem.type === 'folder') {
          addToHistory({ type: 'output', content: `cat: ${args[1]}: Is a directory` });
        } else {
          addToHistory({ type: 'output', content: `cat: ${args[1]}: No such file or directory` });
        }
        break;
      
      case 'echo':
        const echoText = args.slice(1).join(' ').replace(/"/g, '');
        addToHistory({ type: 'output', content: echoText });
        break;
      
      case 'clear':
        setHistory([]);
        break;
      
      case 'open':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'open: missing application name' });
          break;
        }
        
        const appNameToOpen = args[1].toLowerCase();
        const appMap = {
          'browser': { id: 'web-browser', component: 'WebBrowser', url: 'duckduckgo://search' },
          'explorer': { id: 'file-explorer', component: 'FileExplorer' },
          'terminal': { id: 'terminal', component: 'Terminal' },
          'notepad': { id: 'notepad', component: 'Notepad' },
          'youtube': { id: 'privacy-tube', component: 'YouTube' },
          'store': { id: 'store', component: 'Store' },
          'settings': { id: 'settings', component: 'Settings' },
          'ai': { id: 'kiwi-ai', component: 'AIAssistant' }
        };
        
        if (appMap[appNameToOpen]) {
          addToHistory({ type: 'output', content: `Launching ${appNameToOpen}...` });
        } else {
          addToHistory({ type: 'output', content: `Unknown app: ${appNameToOpen}. Available apps: browser, explorer, terminal, notepad, youtube, store, settings, ai` });
        }
        break;
        
      case 'whoami':
        addToHistory({ type: 'output', content: 'user' });
        break;
        
      case 'date':
        addToHistory({ type: 'output', content: new Date().toString() });
        break;
        
      case 'exit':
        addToHistory({ type: 'output', content: 'Closing terminal...' });
        // In a real implementation, this would close the terminal window
        break;
        
      // === NEW UTILITY COMMANDS ===
      case 'guide':
        addToHistory({ type: 'output', content: '==== Welcome to the WebTopOS Terminal Guide ====' });
        addToHistory({ type: 'output', content: 'This interactive guide will help you get started with terminal commands.' });
        
        const guideSteps = [
          'Let\'s start with basic navigation. Type "ls" to list files in the current directory.',
          'Now try moving to a different directory with "cd <directory>".',
          'You can always see your current location with "pwd".',
          'Try viewing a file with "cat <filename>".',
          'To see advanced commands, type "help" for a full list.',
          'You can clear the screen anytime with "clear".',
          'For fun visual effects, try "matrix" or "rain".'
        ];
        
        let stepIndex = 0;
        
        const showNextStep = () => {
          if (stepIndex < guideSteps.length) {
            setTimeout(() => {
              addToHistory({ 
                type: 'output', 
                content: `Step ${stepIndex + 1}: ${guideSteps[stepIndex]}`,
                style: { color: '#00ffaa', fontWeight: 'bold' }
              });
              stepIndex++;
              showNextStep();
            }, 2000);
          } else {
            setTimeout(() => {
              addToHistory({ 
                type: 'output', 
                content: 'Guide completed! You now know the basics of using the terminal.',
                style: { color: '#00ffaa', fontWeight: 'bold' }
              });
              addToHistory({ 
                type: 'output', 
                content: 'For more advanced features, explore the help menu or try the "suggest" command.',
                style: { color: '#00ffaa' }
              });
            }, 2000);
          }
        };
        
        showNextStep();
        break;
        
      case 'calc':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'calc: missing expression' });
          break;
        }
        
        const expr = args.slice(1).join('').replace(/[^0-9+\-*/().]/g, '');
        
        try {
          // Using Function constructor instead of eval for slightly safer evaluation
          const result = Function('"use strict";return (' + expr + ')')();
          addToHistory({ type: 'output', content: `${expr} = ${result}` });
        } catch (error) {
          addToHistory({ type: 'output', content: `Error calculating expression: ${error.message}` });
        }
        break;
        
      case 'todo':
        // Simple todo list manager
        const todoList = JSON.parse(localStorage.getItem('terminal_todo_list') || '[]');
        
        if (!args[1]) {
          // Show todo list
          if (todoList.length === 0) {
            addToHistory({ type: 'output', content: 'Your todo list is empty.' });
          } else {
            addToHistory({ type: 'output', content: 'TODO LIST:' });
            todoList.forEach((item, index) => {
              const status = item.completed ? '[✓]' : '[ ]';
              addToHistory({ 
                type: 'output', 
                content: `${index + 1}. ${status} ${item.text}`
              });
            });
          }
          
          addToHistory({ type: 'output', content: '\nCommands:' });
          addToHistory({ type: 'output', content: '  todo add <text>    - Add a new todo item' });
          addToHistory({ type: 'output', content: '  todo done <number> - Mark an item as completed' });
          addToHistory({ type: 'output', content: '  todo clear         - Clear the todo list' });
          break;
        }
        
        const todoAction = args[1].toLowerCase();
        
        if (todoAction === 'add' && args[2]) {
          const todoText = args.slice(2).join(' ').replace(/"/g, '');
          todoList.push({ text: todoText, completed: false });
          
          localStorage.setItem('terminal_todo_list', JSON.stringify(todoList));
          addToHistory({ type: 'output', content: `Added new todo: "${todoText}"` });
        } else if (todoAction === 'done' && args[2]) {
          const todoIndex = parseInt(args[2]) - 1;
          
          if (isNaN(todoIndex) || todoIndex < 0 || todoIndex >= todoList.length) {
            addToHistory({ type: 'output', content: 'Invalid todo item number.' });
            break;
          }
          
          todoList[todoIndex].completed = true;
          localStorage.setItem('terminal_todo_list', JSON.stringify(todoList));
          addToHistory({ type: 'output', content: `Marked "${todoList[todoIndex].text}" as completed.` });
        } else if (todoAction === 'clear') {
          localStorage.setItem('terminal_todo_list', JSON.stringify([]));
          addToHistory({ type: 'output', content: 'Todo list cleared.' });
        } else {
          addToHistory({ type: 'output', content: `Unknown todo command: ${todoAction}` });
        }
        break;
        
      case 'timer':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'timer: missing duration in seconds' });
          break;
        }
        
        const duration = parseInt(args[1]);
        
        if (isNaN(duration) || duration <= 0) {
          addToHistory({ type: 'output', content: 'Invalid timer duration. Please specify a positive number of seconds.' });
          break;
        }
        
        addToHistory({ type: 'output', content: `Setting timer for ${duration} seconds...` });
        
        let remainingTime = duration;
        const timerInterval = setInterval(() => {
          remainingTime--;
          
          if (remainingTime <= 0) {
            clearInterval(timerInterval);
            addToHistory({ 
              type: 'output', 
              content: `⏰ TIMER FINISHED! ${duration} seconds have elapsed.`,
              style: { fontWeight: 'bold', color: '#ff5555' }
            });
            
            // Try to play a sound or visual alert
            try {
              // Flash the terminal
              const terminalElement = terminalRef.current;
              if (terminalElement) {
                const originalStyle = terminalElement.style.backgroundColor;
                
                let flashCount = 0;
                const flashInterval = setInterval(() => {
                  terminalElement.style.backgroundColor = flashCount % 2 === 0 ? 'rgba(255, 0, 0, 0.3)' : originalStyle;
                  flashCount++;
                  
                  if (flashCount > 5) {
                    clearInterval(flashInterval);
                    terminalElement.style.backgroundColor = originalStyle;
                  }
                }, 200);
              }
            } catch (e) {
              console.error('Error with timer alert:', e);
            }
          } else if (remainingTime <= 5 || remainingTime % 10 === 0) {
            // Show countdown at regular intervals
            addToHistory({ 
              type: 'output', 
              content: `Timer: ${remainingTime} seconds remaining...`,
              replace: true
            });
          }
        }, 1000);
        break;
        
      case 'cheatsheet':
        addToHistory({ type: 'output', content: '===== TERMINAL COMMAND CHEATSHEET =====' });
        
        // File navigation
        addToHistory({ type: 'output', content: '# File Navigation' });
        addToHistory({ type: 'output', content: 'ls                     List files in current directory' });
        addToHistory({ type: 'output', content: 'cd <dir>               Change to directory' });
        addToHistory({ type: 'output', content: 'pwd                    Show current directory' });
        addToHistory({ type: 'output', content: 'cat <file>             Display file contents' });
        
        // Web integration
        addToHistory({ type: 'output', content: '\n# Web Integration' });
        addToHistory({ type: 'output', content: 'fetch <url>            Download content from URL' });
        addToHistory({ type: 'output', content: 'browse <query>         Search the web' });
        addToHistory({ type: 'output', content: 'screenshot             Capture screen image' });
        
        // System tools
        addToHistory({ type: 'output', content: '\n# System Tools' });
        addToHistory({ type: 'output', content: 'sysinfo                Display system information' });
        addToHistory({ type: 'output', content: 'process                List running processes' });
        addToHistory({ type: 'output', content: 'kill <pid>             Terminate a process' });
        
        // Visual effects
        addToHistory({ type: 'output', content: '\n# Visual Effects' });
        addToHistory({ type: 'output', content: 'matrix                 Display Matrix animation' });
        addToHistory({ type: 'output', content: 'rain                   Display digital rain' });
        addToHistory({ type: 'output', content: '3d <model>             Show ASCII 3D model' });
        
        // Utilities
        addToHistory({ type: 'output', content: '\n# Utilities' });
        addToHistory({ type: 'output', content: 'calc <expr>            Calculate expression' });
        addToHistory({ type: 'output', content: 'timer <seconds>        Set countdown timer' });
        addToHistory({ type: 'output', content: 'todo                   Manage todo list' });
        
        addToHistory({ type: 'output', content: '\nFor detailed help on any command, type: help <command>' });
        break;
        
      case 'examples':
        if (!args[1]) {
          addToHistory({ type: 'output', content: 'examples: missing command name' });
          break;
        }
        
        const exampleCommand = args[1].toLowerCase();
        
        // Command examples
        const examples = {
          ls: [
            'ls              # List files in current directory',
            'ls /            # List files in root directory',
            'ls /documents   # List files in documents folder'
          ],
          cd: [
            'cd /            # Go to root directory',
            'cd documents    # Go to documents folder',
            'cd ..           # Go up one directory'
          ],
          fetch: [
            'fetch example.com        # Fetch content from example.com',
            'fetch https://api.example.com/data   # Fetch from API'
          ],
          visualize: [
            'visualize 10,20,15,40,30,25   # Create bar chart',
            'visualize 1,2,3,4,5,6,7,8,9   # Visualize sequence'
          ],
          theme: [
            'theme dark       # Switch to dark theme',
            'theme cyberpunk  # Switch to cyberpunk theme',
            'theme matrix     # Switch to matrix theme'
          ],
          alias: [
            'alias           # Show all aliases',
            'alias ll=ls     # Create alias for ls',
            'alias cls=clear # Create alias for clear'
          ]
        };
        
        if (examples[exampleCommand]) {
          addToHistory({ type: 'output', content: `Examples for "${exampleCommand}":` });
          examples[exampleCommand].forEach(example => {
            addToHistory({ type: 'output', content: example });
          });
        } else {
          addToHistory({ type: 'output', content: `No examples available for "${exampleCommand}".` });
        }
        break;
        
      default:
        if (command) {
          addToHistory({ type: 'output', content: `command not found: ${command}` });
          addToHistory({ type: 'output', content: 'Type "help" to see available commands.' });
        }
        break;
    }
    
    setInput('');
  };

  // Functions for visual effects
  const renderMatrixEffect = () => {
    if (visualEffect !== 'matrix') return null;
    
    // Generate matrix "raindrops"
    const columns = Math.floor(window.innerWidth / 20);
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789$+-*/=%\"'#&_(),.;:?!\\|{}<>[]^~";
    
    let matrixText = '';
    for (let i = 0; i < 15; i++) {
      let line = '';
      for (let j = 0; j < columns; j++) {
        // Random character
        line += chars[Math.floor(Math.random() * chars.length)];
      }
      matrixText += line + '\n';
    }
    
    return (
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          color: '#0f0',
          fontFamily: 'monospace',
          fontSize: '16px',
          zIndex: 100,
          overflow: 'hidden',
          animation: 'fadeIn 0.5s'
        }}
      >
        <pre style={{ whiteSpace: 'pre-wrap' }}>{matrixText}</pre>
      </div>
    );
  };

  const renderRainEffect = () => {
    if (visualEffect !== 'rain') return null;
    
    // Generate digital rain
    const columns = Math.floor(window.innerWidth / 20);
    const chars = "01";
    
    let rainText = '';
    for (let i = 0; i < 20; i++) {
      let line = '';
      for (let j = 0; j < columns; j++) {
        // Random binary
        line += chars[Math.floor(Math.random() * chars.length)];
      }
      rainText += line + '\n';
    }
    
    return (
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          color: '#00aaff',
          fontFamily: 'monospace',
          fontSize: '16px',
          zIndex: 100,
          overflow: 'hidden',
          animation: 'fadeIn 0.5s'
        }}
      >
        <pre style={{ whiteSpace: 'pre-wrap' }}>{rainText}</pre>
      </div>
    );
  };

  // Get the current theme
  const themeStyle = getCurrentTheme();

  return (
    <div 
      className="terminal"
      style={{
        backgroundColor: themeStyle.bg,
        backdropFilter: 'blur(10px)',
        color: themeStyle.text,
        fontFamily: 'monospace',
        padding: '8px',
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
      onClick={() => inputRef.current && inputRef.current.focus()}
      ref={terminalRef}
    >
      {/* Visual Effects */}
      {renderMatrixEffect()}
      {renderRainEffect()}
      
      <div className="terminal-history" style={{ flexGrow: 1 }}>
        {history.map((entry, index) => {
          if (entry.type === 'special') {
            // Handle special effects
            return (
              <div key={index} style={{ margin: '5px 0' }}>
                <pre>{entry.content}</pre>
              </div>
            );
          } else {
            return (
              <div 
                key={index} 
                style={{ 
                  margin: '5px 0',
                  fontWeight: entry.type === 'input' ? 'bold' : 'normal',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: entry.type === 'input' ? themeStyle.prompt : themeStyle.text,
                  ...(entry.style || {})  // Apply any custom styles
                }}
              >
                {entry.content}
              </div>
            );
          }
        })}
      </div>
      
      <div className="terminal-input-line" style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
        <span style={{ fontWeight: 'bold', marginRight: '5px', color: themeStyle.prompt }}>{currentDir}{'>'}</span>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          style={{
            backgroundColor: 'transparent',
            color: themeStyle.text,
            border: 'none',
            outline: 'none',
            fontFamily: 'monospace',
            width: '100%'
          }}
          autoFocus
        />
      </div>
    </div>
  );
};

export default Terminal; 